import { useState, useEffect } from 'react';
import './astyles/SkillsList.css';

interface Skill {
  id: number;
  name: string;
  description?: string;
  created_at: string;
  created_by?: number;
  is_active: boolean;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app';

export default function SkillsList() {
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newSkill, setNewSkill] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    fetchSkills();
  }, []);

  const fetchSkills = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/skills`);
      if (response.ok) {
        const data = await response.json();
        setSkills(data);
      } else {
        setError('Failed to fetch skills');
      }
    } catch (error) {
      console.error('Error fetching skills:', error);
      setError('Failed to fetch skills');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSkill = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSkill.trim()) return;

    setAdding(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/skills`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'user-id': '1' // Admin user ID
        },
        body: JSON.stringify({
          name: newSkill.trim(),
          description: newDescription.trim() || null
        })
      });

      if (response.ok) {
        const newSkillData = await response.json();
        setSkills(prev => [...prev, newSkillData].sort((a, b) => a.name.localeCompare(b.name)));
        setNewSkill('');
        setNewDescription('');
        setShowAddForm(false);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to add skill');
      }
    } catch (error) {
      console.error('Error adding skill:', error);
      setError('Failed to add skill');
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="skills-list">
        <div className="loading">Loading skills...</div>
      </div>
    );
  }

  return (
    <div className="skills-list">
      <div className="list-header">
        <h2>Skills Management</h2>
        <button
          className="add-btn"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          {showAddForm ? 'Cancel' : '+ Add New Skill'}
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {showAddForm && (
        <form onSubmit={handleAddSkill} className="add-form">
          <div className="form-group">
            <label htmlFor="skillName">Skill Name:</label>
            <input
              id="skillName"
              type="text"
              value={newSkill}
              onChange={(e) => setNewSkill(e.target.value)}
              placeholder="Enter skill name"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="skillDescription">Description (optional):</label>
            <textarea
              id="skillDescription"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Enter skill description"
              rows={3}
            />
          </div>
          <div className="form-actions">
            <button type="submit" disabled={adding || !newSkill.trim()}>
              {adding ? 'Adding...' : 'Add Skill'}
            </button>
          </div>
        </form>
      )}

      <div className="skills-grid">
        {skills.length === 0 ? (
          <div className="no-data">No skills found</div>
        ) : (
          skills.map(skill => (
            <div key={skill.id} className="skill-card">
              <div className="skill-header">
                <h3>{skill.name}</h3>
                <span className="skill-id">#{skill.id}</span>
              </div>
              {skill.description && (
                <p className="skill-description">{skill.description}</p>
              )}
              <div className="skill-meta">
                <span className="skill-status">
                  {skill.is_active ? 'Active' : 'Inactive'}
                </span>
                <span className="skill-date">
                  Added: {new Date(skill.created_at).toLocaleDateString()}
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      <div className="list-footer">
        <p>Total Skills: {skills.length}</p>
      </div>
    </div>
  );
}
